/**
 * Choosing which slipping task to offer for a blackjack slot (SPEC.md §9.2).
 *
 * §9.2 says auto-pick "draws from /api/slipping, highest staleness first". Taken literally
 * that is `list[0]`, which is what it used to be — and being literal made it useless. The
 * list is sorted, so every slot in every hand got the same task until it was completed, and
 * the thing at the top is very often at the top *because* you cannot face it. A game that
 * deals you the same unfaceable thing every time stops being played.
 *
 * So the draw is weighted-random rather than sorted-first: staler work is likelier, nothing
 * is certain, and everything you can afford has a real chance of turning up.
 */

import { type SlippingEntry } from "./api";

/**
 * The most any one task's weight can be, however far past its threshold it has drifted.
 *
 * Without a ceiling, one genuinely ancient item swamps the draw — a task 10× over its
 * threshold would take roughly half of every hand, which is the sorted-first behaviour
 * again wearing a disguise. Three means the stalest thing is about three times as likely as
 * something only just slipping, which is enough to feel weighted without being rigged.
 */
export const WEIGHT_CAP = 3;

/**
 * How much this task is favoured, as a multiple of the least-slipping task's chance.
 *
 * Measured as a **ratio** to its own threshold rather than in raw days, so thresholds keep
 * meaning what §8.5 says they mean. A D&D task with a 45-day threshold that is 50 days old
 * is barely slipping; a client task with a 7-day threshold that is 18 days old is badly
 * slipping, and should be likelier despite being the younger of the two.
 */
export function pickWeight(entry: SlippingEntry, cap: number = WEIGHT_CAP): number {
  const threshold = entry.stale_after_days > 0 ? entry.stale_after_days : 1;
  const ratio = entry.days_since_touch / threshold;
  return Math.min(Math.max(ratio, 1), cap);
}

/**
 * Tasks that are not already in another slot and cost no more than `ceiling`.
 *
 * An unrated task is unknown cost, not high cost, so it is always eligible — the same rule
 * the energy slider uses (D35). Hiding it is how tasks get lost.
 */
export function affordable(
  entries: SlippingEntry[],
  ceiling: number,
  taken: ReadonlySet<string>,
): SlippingEntry[] {
  return entries.filter(
    (entry) => !taken.has(entry.id) && (entry.energy == null || entry.energy <= ceiling),
  );
}

/** One weighted draw. `undefined` only when there is nothing to draw from. */
export function weightedPick(
  entries: SlippingEntry[],
  random: () => number = Math.random,
  cap: number = WEIGHT_CAP,
): SlippingEntry | undefined {
  if (entries.length === 0) return undefined;

  const weights = entries.map((entry) => pickWeight(entry, cap));
  const total = weights.reduce((sum, w) => sum + w, 0);

  let roll = random() * total;
  for (let i = 0; i < entries.length; i++) {
    roll -= weights[i];
    if (roll < 0) return entries[i];
  }
  return entries[entries.length - 1]; // floating-point tail
}

export const MAX_ENERGY = 5;

/**
 * Draw one task for a slot, raising the ceiling if nothing that cheap is left.
 *
 * Raising rather than dealing short is deliberate: three tasks is the game (§9), and handing
 * over two because nothing cheap was available would quietly change the rules. `reached`
 * says where it ended up, so the page can admit it went over what you asked for.
 */
export function pickForSlot(
  entries: SlippingEntry[],
  {
    ceiling,
    taken,
    random = Math.random,
  }: { ceiling: number; taken: ReadonlySet<string>; random?: () => number },
): { entry: SlippingEntry | undefined; reached: number } {
  let reached = ceiling;
  let entry = weightedPick(affordable(entries, reached, taken), random);

  while (!entry && reached < MAX_ENERGY) {
    reached += 1;
    entry = weightedPick(affordable(entries, reached, taken), random);
  }
  return { entry, reached };
}
